import { Menu, MenuItem } from '@mui/material';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftSmallerIcon, ArrowRightSmallerIcon, BrowserWebAddIcon } from '../../../icons';
import { Button } from '../../atomic/button';
import { MonthYearPicker, MonthYearPickerHandle } from './monthYearPicker';
import {
  addMonths,
  currentMonth,
  MAX_MONTHS_AHEAD,
  periodForPreset,
  pickFrom,
  pickUntil,
  PRESETS,
  WalletPeriod,
  WalletPreset
} from './walletPeriod';

type Props = { period: WalletPeriod; today: Date; onChange: (period: WalletPeriod) => void };

export const WalletPeriodControl: FC<Props> = ({ period, today, onChange }) => {
  const { t } = useTranslation();
  const presetBoxRef = useRef<HTMLDivElement>(null);
  const fromPickerRef = useRef<MonthYearPickerHandle>(null);
  const untilPickerRef = useRef<MonthYearPickerHandle>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isCustom = period.preset === 'custom';
  const floor = currentMonth(today);
  const ceiling = addMonths(floor, MAX_MONTHS_AHEAD);

  const choosePreset = (preset: WalletPreset) => {
    setMenuOpen(false);
    if (preset === 'custom') {
      onChange({ ...period, preset: 'custom' });
      fromPickerRef.current?.open();
      return;
    }
    onChange(periodForPreset(preset, today));
  };

  return (
    <div className="flex flex-wrap items-center gap-4" data-testid="wallet-period-control">
      <div
        ref={presetBoxRef}
        className="flex h-10 items-center rounded-lg border border-solid border-grey-100 bg-white"
      >
        {/* The arrows open the month pickers; they never step the period. */}
        <Button
          variant="tertiary"
          size="small"
          className="h-10 w-10 p-0"
          aria-label={t('pxtraCard:periodPickFrom')}
          onClick={() => fromPickerRef.current?.open()}
        >
          <ArrowLeftSmallerIcon />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          className="h-10 gap-2 px-2"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          leadingIcon={<BrowserWebAddIcon />}
          onClick={() => setMenuOpen(true)}
        >
          {t(`pxtraCard:period_${period.preset}`)}
        </Button>
        <Button
          variant="tertiary"
          size="small"
          className="h-10 w-10 p-0"
          aria-label={t('pxtraCard:periodPickUntil')}
          onClick={() => untilPickerRef.current?.open()}
        >
          <ArrowRightSmallerIcon />
        </Button>
      </div>
      <Menu
        anchorEl={presetBoxRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        MenuListProps={{ className: 'min-w-[234px]' }}
      >
        {PRESETS.map((preset) => (
          <MenuItem
            key={preset}
            selected={preset === period.preset}
            onClick={() => choosePreset(preset)}
          >
            {t(`pxtraCard:period_${preset}`)}
          </MenuItem>
        ))}
      </Menu>
      <MonthYearPicker
        ref={fromPickerRef}
        label={t('pxtraCard:periodFrom')}
        value={isCustom ? period.from : null}
        min={floor}
        max={ceiling}
        onChange={(from) => onChange(pickFrom(period, from, today))}
      />
      <MonthYearPicker
        ref={untilPickerRef}
        label={t('pxtraCard:periodUntil')}
        value={isCustom ? period.until : null}
        min={isCustom ? period.from : floor}
        max={ceiling}
        onChange={(until) => onChange(pickUntil(period, until, today))}
      />
    </div>
  );
};
